import React, { forwardRef, useImperativeHandle, useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import CONFIG from '../../../config';
import {
  EmbeddedField,
  ErrorText,
  FormColumn,
  FormContainer,
  FormGroup,
  FormInput,
  FormLabel,
  FormRow,
  LoadingText,
} from './gatewayFieldStyles';

function loadScript(src) {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      // If Stripe is already available, resolve immediately
      if (window.Stripe) return resolve();
      // Otherwise, attach listeners to the existing script tag
      const onLoad = () => {
        cleanup();
        resolve();
      };
      const onError = () => {
        cleanup();
        reject(new Error('Failed to load script: ' + src));
      };
      function cleanup() {
        existing.removeEventListener('load', onLoad);
        existing.removeEventListener('error', onError);
      }
      existing.addEventListener('load', onLoad);
      existing.addEventListener('error', onError);
      return;
    }

    const s = document.createElement('script');
    s.src = src;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('Failed to load script: ' + src));
    document.head.appendChild(s);
  });
}

// ===== STYLED COMPONENTS =====
const StripeElementContainer = styled(EmbeddedField)`
  > div {
    width: 100%;
  }
`;

const SaveCardContainer = styled.div`
  display: flex;
  align-items: center;
  gap: var(--zyvgo-spacing-xs, 8px);
`;

const SaveCardCheckbox = styled.input`
  margin: 0;
`;

const SaveCardLabel = styled.span`
  font-size: var(--zyvgo-small-size, 0.875rem);
  color: var(--zyvgo-text-primary, #444444);
`;

const StripeForm = forwardRef((props, ref) => {
  const { gatewayConfig } = props || {};
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cardholderName, setCardholderName] = useState('');
  const [billingZip, setBillingZip] = useState('');
  const stripeRef = useRef(null);
  const elementsRef = useRef(null);
  // separate refs: container DOM nodes for split elements and element instances
  const cardNumberContainerRef = useRef(null);
  const cardExpiryContainerRef = useRef(null);
  const cardCvcContainerRef = useRef(null);

  const cardNumberElementRef = useRef(null);
  const cardExpiryElementRef = useRef(null);
  const cardCvcElementRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    async function init() {
      setLoading(true);
      setError(null);
      try {
        // Prefer canonical env name REACT_APP_STRIPE_PUBLISHABLE_KEY, keep legacy REACT_APP_STRIPE_PUB_KEY as fallback
        const publishableKey =
          gatewayConfig?.stripe_publishable_key ||
          gatewayConfig?.publishable_key ||
          gatewayConfig?.stripe?.publishable_key ||
          gatewayConfig?.data?.stripe_publishable_key ||
          CONFIG.STRIPE_PUBLISHABLE_KEY ||
          null;

        if (!publishableKey) {
          throw new Error('Stripe publishable key not found in payment config or environment (REACT_APP_STRIPE_PUBLISHABLE_KEY)');
        }

        await loadScript('https://js.stripe.com/v3/');

        if (!window.Stripe) {
          throw new Error('Stripe.js failed to load - please check your internet connection');
        }

        // Only create Stripe instance if we don't already have one
        if (!stripeRef.current) {
          stripeRef.current = window.Stripe(publishableKey);
        }

        // Only create elements if we don't already have them
        if (!elementsRef.current) {
          elementsRef.current = stripeRef.current.elements();
        }

        const style = {
          base: {
            color: '#101828',
            fontFamily: 'inherit',
            fontSize: '15px',
            fontSmoothing: 'antialiased',
            lineHeight: '22px',
            '::placeholder': { color: '#98a2b3' },
          },
          invalid: { color: '#b42318', iconColor: '#d92d20' },
        };

        // Create split elements if they don't exist (guard against StrictMode double-mount)
        if (!cardNumberElementRef.current) {
          const num = elementsRef.current.create('cardNumber', { style });
          if (!cardNumberContainerRef.current) throw new Error('Card number container not available');
          num.mount(cardNumberContainerRef.current);
          cardNumberElementRef.current = num;
        }

        if (!cardExpiryElementRef.current) {
          const exp = elementsRef.current.create('cardExpiry', { style });
          if (!cardExpiryContainerRef.current) throw new Error('Card expiry container not available');
          exp.mount(cardExpiryContainerRef.current);
          cardExpiryElementRef.current = exp;
        }

        if (!cardCvcElementRef.current) {
          const cvc = elementsRef.current.create('cardCvc', { style });
          if (!cardCvcContainerRef.current) throw new Error('Card CVC container not available');
          cvc.mount(cardCvcContainerRef.current);
          cardCvcElementRef.current = cvc;
        }

        // Clear any previous errors since elements loaded successfully
        if (mounted) setError(null);
      } catch (err) {
        console.error('Stripe init error', err);
        // Show a friendly error if Stripe.js didn't load; otherwise show generic message
        if (mounted) {
          if (String(err).includes('Stripe.js failed to load')) {
            setError(err.message || String(err));
          } else {
            setError(err.message || 'Failed to initialize payment form');
          }
        }
      } finally {
        if (mounted) setLoading(false);
      }
    }

    // compute a stable publishableKey for dependency tracking so we initialize/reinitialize
    // only when the key changes.
    init();

    return () => {
      mounted = false;
      try {
        if (cardNumberElementRef.current) {
          cardNumberElementRef.current.unmount();
          cardNumberElementRef.current = null;
        }
        if (cardExpiryElementRef.current) {
          cardExpiryElementRef.current.unmount();
          cardExpiryElementRef.current = null;
        }
        if (cardCvcElementRef.current) {
          cardCvcElementRef.current.unmount();
          cardCvcElementRef.current = null;
        }
      } catch (e) {
        // swallow unmount errors
      }
      // keep stripeRef and elementsRef intact to avoid re-creating underlying objects
    };
  }, []); // Remove gatewayConfig dependency to prevent re-initialization

  useImperativeHandle(ref, () => ({
    async getPaymentData() {
      if (!stripeRef.current || !elementsRef.current || !cardNumberElementRef.current) {
        throw new Error('Stripe not initialized');
      }

      if (!cardholderName.trim()) {
        throw new Error('Cardholder name is required');
      }

      const normalizedZip = billingZip.trim().replace(/\D/g, '');
      if (!normalizedZip) {
        throw new Error('Billing ZIP code is required');
      }
      if (!/^\d{5}(\d{4})?$/.test(normalizedZip)) {
        throw new Error('Please enter a valid ZIP code');
      }

      setError(null);
      try {
        const cardEl = cardNumberElementRef.current;
        const res = await stripeRef.current.createPaymentMethod({
          type: 'card',
          card: cardEl,
          billing_details: {
            name: cardholderName.trim(),
            address: {
              postal_code: normalizedZip,
            },
          },
        });
        if (res.error) {
          throw res.error;
        }
        return { payment_method_id: res.paymentMethod.id, postal_code: normalizedZip };
      } catch (err) {
        console.error('Stripe tokenization error', err);
        throw new Error(err.message || 'Payment method creation failed');
      }
    },
  }));

  return (
    <FormContainer>
      <FormGroup>
        <FormLabel htmlFor="cardholder-name">
          Cardholder Name
        </FormLabel>
        <FormInput
          type="text"
          id="cardholder-name"
          placeholder="John Doe"
          value={cardholderName}
          onChange={(e) => setCardholderName(e.target.value)}
        />
      </FormGroup>

      <FormGroup>
        <FormLabel htmlFor="card-number">Card number</FormLabel>
        <StripeElementContainer ref={cardNumberContainerRef} id="card-number" />
      </FormGroup>

      <FormRow>
        <FormColumn>
          <FormLabel htmlFor="card-expiry">Expiration Date</FormLabel>
          <StripeElementContainer ref={cardExpiryContainerRef} id="card-expiry" />
        </FormColumn>
        <FormColumn>
          <FormLabel htmlFor="card-cvc">CVC</FormLabel>
          <StripeElementContainer ref={cardCvcContainerRef} id="card-cvc" />
        </FormColumn>
      </FormRow>

      <FormGroup>
        <FormLabel htmlFor="billing-zip">Billing ZIP Code</FormLabel>
        <FormInput
          type="text"
          id="billing-zip"
          placeholder="12345"
          value={billingZip}
          onChange={(e) => setBillingZip(e.target.value)}
          inputMode="numeric"
        />
      </FormGroup>

      {loading && <LoadingText>Loading payment form...</LoadingText>}
      {error && <ErrorText>{error}</ErrorText>}
    </FormContainer>
  );
});

export default StripeForm;
